import React, { useState, useEffect, ReactElement, CSSProperties } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import * as types from '../../constants/actionTypes';
import { actions as companyDashboardActions } from '../../actions/companyDashboardActions';
import { actions as locationTaskActions } from '../../actions/locationTaskActions';
import './ManageCorporateViews.scss';

interface DashboardUserFilters {
    filters: Array<UserFilter>
}

interface UserFilter {
    name: string;
    ordinal: number;
    id: number;
    isCustom: boolean;
}

interface ManageCorporateViewsProps {
    setLastViewDeleted?: (lastItemIdDeleted: string) => void;
}

const ManageCorporateViews = ({ setLastViewDeleted }: ManageCorporateViewsProps): ReactElement => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userFilters, setUserFilters] = useState([]);
    const [lastItemIdDeleted, setLastItemIdDeleted] = useState('');

    const dispatch: Dispatch = useDispatch();
    const getSettings = async () => dispatch(companyDashboardActions.listSettingsV2());
    const saveSettings = async (filters: DashboardUserFilters) => dispatch(companyDashboardActions.saveCompanyDashboardCustomViews(filters));
    const setTasksPageTaskMenuItems = async (items: Array<any>) => dispatch(locationTaskActions.setTasksPageTaskMenuItems(items));
    const setTasksMenuSelected = async (selectedValue: string) => dispatch(locationTaskActions.setTasksMenuSelected(selectedValue));

    const history = useHistory();

    async function fetchUserFilters(setUserFilters, setIsLoading) {
        try {
            const response = await getSettings();
            if (response.type === types.LIST_COMPANY_DASHBOARD_SETTINGS_SUCCESS) {
                setUserFilters(response.payload);
            } else if (response.type === types.LIST_COMPANY_DASHBOARD_SETTINGS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsLoading(false);
        }
    }    

    useEffect(() => {
        fetchUserFilters(setUserFilters, setIsLoading);
        document.title = 'Manage Corporate Views';
    }, []); // Empty dependency array means this effect runs only once after initial render

    const { tasksMenuSelectedValue } = useSelector((state) => state.taskListState);    

    const onDragEnd = async (result) => {
        if (!result.destination) {
            return; // dropped outside the step list area
        }

        const filters = [...userFilters];
        const [removed] = filters.splice(result.source.index, 1);

        filters.splice(result.destination.index, 0, removed);

        // Re-number the ordinals
        for (let i = 0; i < filters.length; i++) {
            filters[i].ordinal = i + 1;
        }

        try {
            const response = await saveSettings(filters);
            if (response.type === types.SAVE_CUSTOM_VIEWS_COMPANY_DASHBOARD_SETTINGS_SUCCESS) {
                Notification('success', '', 'Filters saved');
            } else if (response.type === types.SAVE_CUSTOM_VIEWS_COMPANY_DASHBOARD_SETTINGS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsLoading(false);
        }

        setUserFilters(filters);
    };

    const getListStyle = isDraggingOver => ({
        background: isDraggingOver ? '#eee' : 'white',
        borderRadius: '0.25rem',
    });

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: 'none',
        lineHeight: 1,
        margin: `0 0 2px 0`,
        background: 'white',
        border: isDragging ? '2px solid #03a9f4' : '1px solid #DEE2E6',
        // styles we need to apply on draggables
        ...draggableStyle,
    });

    const trashIconStyle = (filter: UserFilter): CSSProperties => {
        return {
            visibility: filter.isCustom ? 'visible' : 'hidden',
            cursor: 'pointer'
        }
    }

    const onDeleteFilter = async (ordinal: number) => {
        const filter = userFilters.find(f => f.ordinal === ordinal);
        const filterName = filter.name;     // We want this to blow up if it's null
        const filters = userFilters.filter(f => f.ordinal !== ordinal);

        // Re-number the ordinals
        for (let i = 0; i < filters.length; i++) {
            filters[i].ordinal = i + 1;
        }

        try {
            // Save the new list
            const saveResponse = await saveSettings(filters);
            if (saveResponse.type === types.SAVE_CUSTOM_VIEWS_COMPANY_DASHBOARD_SETTINGS_SUCCESS) {
                Notification('success', '', 'Custom view updated');
                setLastItemIdDeleted(filter.id);
            } else if (saveResponse.type === types.SAVE_CUSTOM_VIEWS_COMPANY_DASHBOARD_SETTINGS_FAILURE) {
                Notification("error", "Error", saveResponse.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            setIsLoading(false);
        }

        setUserFilters(filters);

        // We need to update the task page filter names in redux
        const menuItems = filters.map(f => ({ filterName: f.name, isCustom: f.isCustom }));
        await setTasksPageTaskMenuItems(menuItems || []);

        // Finally if user has deleted the filter that is the current tasks menu selected value, we must reset it to default
        if (filterName === tasksMenuSelectedValue)
            await setTasksMenuSelected('');
    }

    interface FilterProps {
        filter: UserFilter;
        index: number;
    }

    function Filter({ filter, index }: FilterProps) {
        return (
            // The default filter isn't re-orderable
            filter.isDefault ? (
                <li className="list-group-item d-flex justify-content-between" style={getItemStyle(false, {})}>
                    <i className="fas fa-bars mr-4" style={{ visibility: 'hidden' }}></i>
                    <span className="flex-grow-1">{filter.name}</span>
                    <i className='far fa-trash-can' style={trashIconStyle(filter)} onClick={() => onDeleteFilter(filter.ordinal)}></i>
                </li>
            ) : (
                <Draggable key={filter.ordinal} draggableId={String(filter.ordinal)} index={index}>
                    {(provided, snapshot) => (
                        <li 
                            className="list-group-item d-flex justify-content-between" 
                            ref={provided.innerRef} 
                            {...provided.draggableProps} 
                            {...provided.dragHandleProps} 
                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                        >
                            <i className="fas fa-bars mr-4" style={{paddingRight:"10px"}}></i>
                            <span className="flex-grow-1">{filter.name}</span>
                            <i 
                                className="far fa-trash-can ml-2" 
                                style={{
                                    visibility: 'visible',
                                    cursor: 'pointer'
                                }} 
                                onClick={() => onDeleteFilter(filter.ordinal)}
                            ></i>
                        </li>
                    )}
                </Draggable>
            )
        );
    }

    const handleNavigation = () => {
        // Rendered inside CorporateDashboardPage it receives setLastViewDeleted;
        // reached as a route (e.g. from Overview) it has no parent setter, so just go back.
        if (setLastViewDeleted) {
            setLastViewDeleted(lastItemIdDeleted || '');

            if (lastItemIdDeleted) {
                fetchUserFilters(setUserFilters, setIsLoading);
            }
        } else {
            history.goBack();
        }
    };

    return (
        <div className='manage-task-views'>
            <LoadingIndicator isLoading={isLoading} isFullscreen={true}>
                <div>
                    <div className='mtv-breadcrumb row no-gutters'>
                        <div className='col-12'>
                            <a onClick={handleNavigation}>
                                <i className="far fa-chevron-left"></i>
                                <span>Go Back</span>
                            </a>
                        </div>
                    </div>

                    <div className='heading-nav row no-gutters'>
                        <div className='col'>
                            <div className='heading-title'>Manage Saved Views</div>
                        </div>
                    </div>

                    <div className='mtv-main row no-gutters'>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId='droppable'>
                                {(provided = {}, snapshot = { isDraggingOver: false }) => (
                                    <ul
                                        className="list-group col-sm-8"
                                        ref={provided.innerRef}
                                        style={getListStyle(snapshot.isDraggingOver)}
                                        {...provided.droppableProps}
                                    >
                                        {userFilters?.map((filter, index) => (
                                            <Filter filter={filter} index={index} key={filter.ordinal} />
                                        ))}
                                        {provided.placeholder}
                                    </ul>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                </div>
            </LoadingIndicator>
        </div>
    );
}

export default ManageCorporateViews;
